// =============================================================================
// features/sales/campaigns/wizard/components/WizardStepShell.tsx — Casca
// comum a toda etapa: título, corpo e rodapé de navegação (Voltar/Continuar).
// Erro fica DENTRO da casca, acima do rodapé — nunca substitui o formulário
// (DoD: "erro de validação não descarta o que o usuário já digitou").
// =============================================================================

import * as React from 'react';

import { Button } from '../../../../../components/ui/Button';
import { cn } from '../../../../../lib/cn';

export interface WizardStepShellProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  onBack?: (() => void) | undefined;
  onContinue: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  isPending?: boolean;
  /** Nunca limpa o formulário — só aparece ACIMA do rodapé, form intacto atrás. */
  errorMessage?: string | null;
}

export function WizardStepShell({
  title,
  description,
  children,
  onBack,
  onContinue,
  continueLabel = 'Continuar',
  continueDisabled = false,
  isPending = false,
  errorMessage = null,
}: WizardStepShellProps): React.JSX.Element {
  return (
    <div
      className="rounded-md border border-border overflow-hidden"
      style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-2)' }}
    >
      <div
        className="flex flex-col gap-1 px-5 sm:px-7 py-5"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <h2
          className="font-display font-bold text-ink"
          style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
        >
          {title}
        </h2>
        {description && <p className="font-sans text-sm text-ink-3">{description}</p>}
      </div>

      <div className="flex flex-col gap-5 px-5 sm:px-7 py-6">{children}</div>

      {errorMessage && (
        <div className="px-5 sm:px-7">
          <div
            role="alert"
            className="rounded-sm px-4 py-3 border-l-[3px] mb-2"
            style={{
              borderColor: 'var(--danger)',
              background: 'var(--danger-bg)',
              boxShadow: 'var(--elev-1)',
            }}
          >
            <p className="font-sans font-semibold text-sm text-danger">
              Não foi possível continuar
            </p>
            <p className="font-sans text-xs text-ink-2 mt-0.5">{errorMessage}</p>
            <p className="font-sans text-xs text-ink-3 mt-1">
              Nada do que você preencheu foi perdido — corrija e tente de novo.
            </p>
          </div>
        </div>
      )}

      <div
        className={cn('flex items-center gap-3 px-5 sm:px-7 py-5 flex-col-reverse sm:flex-row')}
        style={{ borderTop: '1px solid var(--border)', background: 'var(--surface-muted)' }}
      >
        {onBack ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onBack}
            disabled={isPending}
            className="w-full sm:w-auto"
          >
            Voltar
          </Button>
        ) : (
          <span className="hidden sm:block" />
        )}
        <Button
          type="button"
          variant="primary"
          onClick={onContinue}
          disabled={continueDisabled || isPending}
          className="w-full sm:w-auto sm:ml-auto"
        >
          {isPending ? 'Salvando…' : continueLabel}
        </Button>
      </div>
    </div>
  );
}
